import React from "react";
import { Paper } from "@mui/material";
import ActionRibbon from "@components/ActionRibbon/ActionRibbon";
import Badge from "@components/Badge/Badge";
import ErrorHandler from "@components/ErrorHandler/ErrorHandler";
import { useUploadStore } from "@components/FileUpload/uploadStore";
import AddReceiptButton from "@components/FlowActionButtons/AddReceiptButton";
import PaperItem from "@components/Paper/PaperItem";
import Subheader from "@components/Subheader/Subheader";
import UnauthorizedRedirection from "@components/UnauthorizedRedirection/UnauthorizedRedirection";
import { useNotification } from "@context/NotificationContext";
import PageLayout from "@layouts/PageLayout";
import { useAuthStore } from "@persisted-state/auth/auth.store";
import { uploadFileToAzureBlob } from "@services/azureDataApi";
import type { LicenseResponse, VendorLicenseTaskDecision } from "@services/licensing/types";
import {
  getPortOperatorLicenseById,
  getVendorLicenseById,
  postPortOperatorLicenseReceipt,
} from "@services/licensingApi";
import { SasTokenPrefix, SasTokenPrivilege } from "@services/user/types";
import { getSasToken } from "@services/userApi";
import { useMutation, useQuery } from "@tanstack/react-query";
import { runTransaction } from "@telemetry/runTransaction";
import { isForbidden } from "@utils/errorsHandler";
import { paths } from "@utils/navigation";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useParams } from "react-router-dom";
import LicenseCard from "./components/LicenseCard";
import ApproveLicenseDrawer from "./flow/approveLicense/ApproveLicenseDrawer";
import ApprovePortOperatorLicenseDrawer from "./flow/approveLicense/ApprovePortOperatorLicenseDrawer";
import LicenseFlowButtons from "./flow/LicenseFlowButtons";
import ReceiptUploadDrawer from "./flow/receiptUpload/ReceiptUploadDrawer";
import RejectLicenseDrawer from "./flow/rejectLicense/RejectLicenseDrawer";
import { useCompleteLicense } from "./flow/useCompleteLicense";
import { canUserUploadReceipt, LICENSE_STATUSES } from "./flow/utils";
import { useDownloadLicenseDocument } from "./hooks/useDownloadLicenseDocument";
import StatusDetailsDrawer, { hasStatusDetails } from "./StatusDetailsDrawer";
import {
  buildVendorLicenseDetails,
  getAdditionalInformationItems,
  getApplicantDetailsItems,
  getCompanyDetailsItems,
  getDocumentTitle,
  getFileExtensionFromUrl,
  getLicenseStatusFormat,
  getTradeLicenseItems,
  sortLicenseDocumentsForDisplay,
} from "./utils";

const TEST_ID_BASE = "licensing-page";

const transStyleClass = "font-normal text-[#646A77]";
const wrapperStyleClass = "border-t border-[#F1F2F4] pt-3";
const wrapperFirstStyleClass = "pt-3";

const buildDecisionFromCompletePayload = (payload: any): VendorLicenseTaskDecision | null => {
  if (!payload?.status) return null;

  if (payload.status === "APPROVED") {
    return {
      status: "APPROVED",
      startDate: payload.startDate,
      expiryDate: payload.expiryDate,
      numberOfAccountsAllowed: payload.numberOfAccountsAllowed,
      companyId: payload.companyId,
      approvalNotes: payload.approvalNotes,
    };
  }

  if (payload.status === "REJECTED") {
    return {
      status: "REJECTED",
      rejectionNotes: payload.rejectionNotes,
    };
  }

  return null;
};

const mapFakeStatusForContext = ({
  status,
  isPortOperatorContext,
  previousStatus,
}: {
  status?: string;
  isPortOperatorContext?: boolean;
  previousStatus?: string;
}): string | undefined => {
  if (!status) return undefined;
  if (!isPortOperatorContext) return status;
  if (status === "APPROVED") {
    return previousStatus === "AWAITING_MANAGING_DIRECTOR_APPROVAL"
      ? "APPROVED"
      : "AWAITING_UPLOAD_RECEIPT";
  }
  if (status === "REJECTED") return "REJECTED_BY_MANAGING_DIRECTOR";

  return status;
};

const getErrorDetail = (err: unknown): string | undefined => {
  const error = err as {
    response?: { data?: { detail?: unknown; message?: unknown } };
    data?: { detail?: unknown };
    detail?: unknown;
    message?: unknown;
  };

  const detail =
    error.response?.data?.detail ??
    error.response?.data?.message ??
    error.data?.detail ??
    error.detail ??
    error.message;

  return typeof detail === "string" && detail.trim().length > 0 ? detail : undefined;
};

const LicenseDetails: React.FC = () => {
  const { i18n, t } = useTranslation();
  const dir = i18n.language === "ar" ? "rtl" : "ltr";

  const { notify } = useNotification();
  const { user } = useAuthStore();
  const { pathname, search } = useLocation();
  const { id } = useParams();
  const submittedBy = user?.preferredUsername ?? "";

  const { handleDownload, handlePreview } = useDownloadLicenseDocument();

  const [openDrawers, setOpenDrawers] = React.useState<{ [key: string]: boolean }>({
    rejectDrawer: false,
    approveDrawer: false,
    detailsDrawer: false,
    receiptUploadDrawer: false,
  });

  const [rejectionAttachmentUrl, setRejectionAttachmentUrl] = React.useState<string | undefined>();
  const [approvalAttachmentUrl, setApprovalAttachmentUrl] = React.useState<string | undefined>();
  const [receiptAttachmentUrl, setReceiptAttachmentUrl] = React.useState<string | undefined>();
  const receiptAttachmentUrlRef = React.useRef<string | undefined>(undefined);
  const fakeStatusSourceStatusRef = React.useRef<string | null>(null);

  const [fakeStatus, setFakeStatus] = React.useState<string | null>(null);
  const [fakeTaskDecision, setFakeTaskDecision] = React.useState<VendorLicenseTaskDecision | null>(
    null,
  );

  const openDrawer = (drawerName: string) =>
    setOpenDrawers((prev) => ({ ...prev, [drawerName]: true }));
  const closeDrawer = (drawerName: string) =>
    setOpenDrawers((prev) => ({ ...prev, [drawerName]: false }));

  const clearRejectionAttachmentUrl = React.useCallback(() => {
    setRejectionAttachmentUrl(undefined);
  }, []);

  const clearApprovalAttachmentUrl = React.useCallback(() => {
    setApprovalAttachmentUrl(undefined);
  }, []);

  const clearDecisionAttachmentUrls = React.useCallback(() => {
    clearRejectionAttachmentUrl();
    clearApprovalAttachmentUrl();
  }, [clearApprovalAttachmentUrl, clearRejectionAttachmentUrl]);

  const closeDecisionDrawers = React.useCallback(() => {
    clearDecisionAttachmentUrls();
    closeDrawer("rejectDrawer");
    closeDrawer("approveDrawer");
  }, [clearDecisionAttachmentUrls]);

  const clearReceiptAttachmentUrl = React.useCallback(() => {
    receiptAttachmentUrlRef.current = undefined;
    setReceiptAttachmentUrl(undefined);
  }, []);

  const isVendorMaster = user?.roles?.includes("vendor-master");
  const isServiceDesk = user?.roles?.includes("service-desk");
  const isManagingDirectorOffice = user?.roles?.includes("managing-director-office");

  const isVendorMasterContext = isVendorMaster || pathname.includes(paths.licenses.index);
  const isServiceDeskContext = isServiceDesk || pathname.includes(paths.licenses.accountIndex);
  const isPortOperatorContext =
    isManagingDirectorOffice || pathname.includes(paths.licenses.portOperatorIndex);
  const selectedLicenseType = React.useMemo(
    () => new URLSearchParams(search).get("licenseType"),
    [search],
  );

  const shouldUsePortOperatorEndpoint =
    isPortOperatorContext || selectedLicenseType === "PORT_OPERATOR_LICENSE";

  const licensesBasePath = isPortOperatorContext
    ? `/${paths.licenses.portOperatorIndex}`
    : isServiceDeskContext
      ? `/${paths.licenses.accountIndex}`
      : `/${paths.licenses.index}`;

  const {
    data: licenseResponse,
    refetch: refetchLicense,
    isError: isLicenseError,
    error: licenseError,
  } = useQuery<LicenseResponse>({
    queryKey: [
      "Licensing",
      shouldUsePortOperatorEndpoint ? "port-operator" : "vendor",
      id,
      selectedLicenseType,
    ],
    queryFn: () => {
      if (shouldUsePortOperatorEndpoint) {
        return getPortOperatorLicenseById(id as string);
      }
      return getVendorLicenseById(id as string);
    },
    enabled: isVendorMaster ? true : !!id,
    refetchInterval: 60_000,
    staleTime: 0,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
  });
  React.useEffect(() => {
    if (!fakeStatus && !fakeTaskDecision) return;
    if (!licenseResponse) return;

    const serverStatus = licenseResponse.status;
    const serverDecision = licenseResponse.taskDecision;

    const serverHasTerminalDecision =
      Boolean(serverDecision) &&
      (serverDecision?.status === "APPROVED" || serverDecision?.status === "REJECTED");

    if (
      fakeStatus === LICENSE_STATUSES.AWAITING_UPLOAD_RECEIPT &&
      serverStatus !== LICENSE_STATUSES.AWAITING_UPLOAD_RECEIPT &&
      serverStatus !== fakeStatusSourceStatusRef.current
    ) {
      fakeStatusSourceStatusRef.current = null;
      setFakeStatus(null);
    }

    if (serverHasTerminalDecision) {
      fakeStatusSourceStatusRef.current = null;
      setFakeTaskDecision(null);

      if (!fakeStatus || serverStatus === fakeStatus) {
        setFakeStatus(null);
      }
    }
  }, [licenseResponse, fakeStatus, fakeTaskDecision]);

  const { completeLicense, isPending: isCompletingLicense } = useCompleteLicense({
    handleSuccess: async (status, variables) => {
      closeDecisionDrawers();

      const previousStatus = licenseResponse?.status;
      const isPortOperatorApproval = status === "APPROVED" && variables?.isPortOperatorContext;
      const isManagingDirectorVerificationApproval =
        isPortOperatorApproval && previousStatus === "AWAITING_MANAGING_DIRECTOR_VERIFICATION";

      if (status && variables?.payload) {
        const decision = buildDecisionFromCompletePayload(variables.payload);
        const mappedStatus = mapFakeStatusForContext({
          status,
          isPortOperatorContext: variables?.isPortOperatorContext,
          previousStatus,
        }) as string;

        fakeStatusSourceStatusRef.current =
          mappedStatus === LICENSE_STATUSES.AWAITING_UPLOAD_RECEIPT
            ? (previousStatus ?? null)
            : null;
        setFakeStatus(mappedStatus);

        if (
          decision &&
          (status === "REJECTED" || (status === "APPROVED" && !isPortOperatorApproval))
        ) {
          setFakeTaskDecision(decision);
        } else if (isPortOperatorApproval) {
          setFakeTaskDecision(null);
        }
      }

      if (isManagingDirectorVerificationApproval) {
        notify({
          title: t("licensing.notifications.port-operator-license-verified-title"),
          description: t("licensing.notifications.port-operator-license-verified-description"),
          type: "success",
        });
      }

      if (isPortOperatorApproval && previousStatus === "AWAITING_MANAGING_DIRECTOR_APPROVAL") {
        notify({
          title: t("licensing.notifications.port-operator-license-approved-title"),
          description: t("licensing.notifications.port-operator-license-approved-description"),
          type: "success",
        });
      }

      if (status === "REJECTED") {
        notify({
          title: variables?.isPortOperatorContext
            ? t("licensing.notifications.port-operator-license-rejected-title")
            : t("licensing.notifications.vendor-license-rejected-title"),
          description: variables?.isPortOperatorContext
            ? t("licensing.notifications.port-operator-license-rejected-description")
            : t("licensing.notifications.vendor-license-rejected-description"),
          type: "success",
        });
      }

      if (status === "APPROVED" && !variables?.isPortOperatorContext) {
        notify({
          title: t("licensing.notifications.vendor-license-approved-title"),
          description: t("licensing.notifications.vendor-license-approved-description"),
          type: "success",
        });
      }

      const refetchResult = await refetchLicense();
      const refetchedDecision = refetchResult?.data?.taskDecision;

      if (
        refetchedDecision &&
        (refetchedDecision.status === "APPROVED" || refetchedDecision.status === "REJECTED")
      ) {
        setFakeTaskDecision(null);
      }
    },
    handleError: (err) => {
      closeDecisionDrawers();

      const detail = getErrorDetail(err);

      notify({
        title: t("licensing.notifications.vendor-license-complete-error-title"),
        description:
          typeof detail === "string" && detail.length > 0
            ? detail
            : t("licensing.notifications.vendor-license-complete-error-description"),
        type: "error",
      });
    },
  });

  const rejectionUploadMutation = useMutation({
    mutationFn: async ({ file }: { file: File }) => {
      return runTransaction(
        {
          module: "licensing",
          screen: "LicenseDetails",
          action: "upload_license_document",
          entityType: "license_document",
        },
        async (transactionId) => {
          const payloadForSasToken = {
            prefix: SasTokenPrefix.LICENSE_SERVICE,
            contentType: file.type || null,
            privilege: SasTokenPrivilege.WRITE,
            blobUrl: null,
          };

          const { blobUrl, sasToken } = await getSasToken(
            payloadForSasToken,
            ...(transactionId ? [transactionId] : []),
          );

          const [uploadResult] = await uploadFileToAzureBlob({
            file,
            blobUrl,
            sasToken,
            certificateType: "REJECTION_NOTES",
          });

          return uploadResult?.documentUrl ?? blobUrl;
        },
      );
    },
  });

  const approvalUploadMutation = useMutation({
    mutationFn: async ({ file }: { file: File }) => {
      return runTransaction(
        {
          module: "licensing",
          screen: "LicenseDetails",
          action: "upload_license_document",
          entityType: "license_document",
        },
        async (transactionId) => {
          const payloadForSasToken = {
            prefix: SasTokenPrefix.LICENSE_SERVICE,
            contentType: file.type || null,
            privilege: SasTokenPrivilege.WRITE,
            blobUrl: null,
          };

          const { blobUrl, sasToken } = await getSasToken(
            payloadForSasToken,
            ...(transactionId ? [transactionId] : []),
          );

          const [uploadResult] = await uploadFileToAzureBlob({
            file,
            blobUrl,
            sasToken,
            certificateType: "APPROVAL_NOTES",
          });

          return uploadResult?.documentUrl ?? blobUrl;
        },
      );
    },
  });

  const receiptUploadMutation = useMutation({
    mutationFn: async ({ file }: { file: File }) => {
      return runTransaction(
        {
          module: "licensing",
          screen: "LicenseDetails",
          action: "upload_license_document",
          entityType: "license_document",
        },
        async (transactionId) => {
          const payloadForSasToken = {
            prefix: SasTokenPrefix.LICENSE_SERVICE,
            contentType: file.type || null,
            privilege: SasTokenPrivilege.WRITE,
            blobUrl: null,
          };

          const { blobUrl, sasToken } = await getSasToken(
            payloadForSasToken,
            ...(transactionId ? [transactionId] : []),
          );

          const [uploadResult] = await uploadFileToAzureBlob({
            file,
            blobUrl,
            sasToken,
            certificateType: "RECEIPT",
          });

          return uploadResult?.documentUrl ?? blobUrl;
        },
      );
    },
  });

  const receiptSubmitMutation = useMutation({
    mutationFn: async ({
      receiptNumber,
      receiptDate,
      receiptAmount,
      receiptDocumentUrl,
    }: {
      receiptNumber: string;
      receiptDate: string;
      receiptAmount: string;
      receiptDocumentUrl: string;
    }) => {
      if (!id) {
        throw new Error(t("common.something-went-wrong"));
      }

      const receiptAmountAsNumber = Number.parseFloat(receiptAmount);
      const payload = {
        receiptNumber,
        receiptDate,
        receiptAmount: receiptAmountAsNumber,
        receiptDocumentUrl,
        createdAt: new Date().toISOString(),
      };

      return runTransaction(
        {
          module: "licensing",
          screen: "LicenseDetails",
          action: "submit_port_operator_license_receipt",
          entityType: "license_receipt",
          entityId: id,
        },
        (transactionId) =>
          postPortOperatorLicenseReceipt({
            id,
            payload,
            ...(transactionId ? { transactionId } : {}),
          }),
      );
    },
    onSuccess: async () => {
      closeDrawer("receiptUploadDrawer");
      clearReceiptAttachmentUrl();

      notify({
        title: t("licensing.notifications.receipt-upload-success-title"),
        description: t("licensing.notifications.receipt-upload-success-description"),
        type: "success",
      });

      await refetchLicense();
    },
    onError: (err) => {
      notify({
        title: t("licensing.notifications.receipt-upload-error-title"),
        description: getErrorDetail(err) ?? t("common.something-went-wrong"),
        type: "error",
      });
    },
  });

  const handleRejectionDocumentUpload = (file: File) => {
    const uploadState = useUploadStore.getState();
    uploadState.beginUploadInFlight("licenseRejectionAttachment");
    setRejectionAttachmentUrl(undefined);

    rejectionUploadMutation.mutate(
      { file },
      {
        onSuccess: (url) => setRejectionAttachmentUrl(url),
        onError: () => {
          setRejectionAttachmentUrl(undefined);
          useUploadStore.getState().clearFilesForKey("licenseRejectionAttachment");
        },
        onSettled: () => {
          useUploadStore.getState().endUploadInFlight("licenseRejectionAttachment");
        },
      },
    );
  };

  const handleApprovalDocumentUpload = (file: File) => {
    const uploadState = useUploadStore.getState();
    uploadState.beginUploadInFlight("licenseApprovalAttachment");
    setApprovalAttachmentUrl(undefined);

    approvalUploadMutation.mutate(
      { file },
      {
        onSuccess: (url) => setApprovalAttachmentUrl(url),
        onError: () => {
          setApprovalAttachmentUrl(undefined);
          useUploadStore.getState().clearFilesForKey("licenseApprovalAttachment");
        },
        onSettled: () => {
          useUploadStore.getState().endUploadInFlight("licenseApprovalAttachment");
        },
      },
    );
  };

  const handleReceiptDocumentUpload = (file: File) => {
    clearReceiptAttachmentUrl();
    const uploadState = useUploadStore.getState();
    uploadState.beginUploadInFlight("licenseReceiptAttachment");
    receiptUploadMutation.mutate(
      { file },
      {
        onSuccess: (url) => {
          receiptAttachmentUrlRef.current = url;
          setReceiptAttachmentUrl(url);
        },
        onError: (err) => {
          useUploadStore.getState().clearFilesForKey("licenseReceiptAttachment");
          notify({
            title: t("licensing.notifications.receipt-upload-error-title"),
            description: getErrorDetail(err) ?? t("common.something-went-wrong"),
            type: "error",
          });
        },
        onSettled: () => {
          useUploadStore.getState().endUploadInFlight("licenseReceiptAttachment");
        },
      },
    );
  };

  const handleRejectLicense = async ({ rejectionNotes }: { rejectionNotes: string }) => {
    if (!id) return;

    const rejectionFile = useUploadStore.getState().getFilesForKey("licenseRejectionAttachment")[0];
    if (rejectionFile instanceof File && !rejectionAttachmentUrl) {
      notify({
        title: t("common.notification.please-wait-for-upload"),
        type: "warning",
      });
      return;
    }

    const rejectionCompanyId = isServiceDeskContext
      ? (licenseResponse?.companyId ?? licenseResponse?.taskDecision?.companyId ?? undefined)
      : undefined;

    completeLicense({
      licenseId: id,
      isPortOperatorContext,
      payload: {
        status: "REJECTED",
        rejectionNotes,
        rejectionAttachmentUrl,
        ...(rejectionCompanyId ? { companyId: rejectionCompanyId } : {}),
        submittedBy,
      },
    });
  };

  const handleApproveLicense = async (payload: {
    startDate: string;
    expiryDate: string;
    numberOfAccountsAllowed: number;
    companyId: string;
    approvalNotes?: string;
  }) => {
    if (!id) return;

    const approvalFile = useUploadStore.getState().getFilesForKey("licenseApprovalAttachment")[0];
    if (approvalFile instanceof File && !approvalAttachmentUrl) {
      notify({
        title: t("common.notification.please-wait-for-upload"),
        type: "warning",
      });
      return;
    }

    completeLicense({
      licenseId: id,
      isPortOperatorContext,
      payload: {
        status: "APPROVED",
        startDate: payload.startDate,
        expiryDate: payload.expiryDate,
        numberOfAccountsAllowed: payload.numberOfAccountsAllowed,
        companyId: payload.companyId,
        approvalNotes: payload.approvalNotes,
        approvalAttachmentUrl,
        submittedBy,
      },
    });
  };

  const handleApprovePortOperatorLicense = async ({
    startDate,
    expiryDate,
    approvalNotes,
  }: {
    startDate?: string;
    expiryDate?: string;
    approvalNotes?: string;
  }) => {
    if (!id) return;

    const approvalFile = useUploadStore.getState().getFilesForKey("licenseApprovalAttachment")[0];
    if (approvalFile instanceof File && !approvalAttachmentUrl) {
      notify({
        title: t("common.notification.please-wait-for-upload"),
        type: "warning",
      });
      return;
    }

    completeLicense({
      licenseId: id,
      isPortOperatorContext,
      payload: {
        status: "APPROVED",
        ...(startDate ? { startDate } : {}),
        ...(expiryDate ? { expiryDate } : {}),
        approvalNotes,
        approvalAttachmentUrl,
        submittedBy,
      },
    });
  };

  const handlePrint = () => {
    try {
      if (typeof window !== "undefined" && typeof window.print === "function") {
        window.print();
        return;
      }

      notify({
        title: t("common.error"),
        description: t("common.unable-to-open-print-window"),
        type: "error",
      });
    } catch {
      notify({
        title: t("common.error"),
        description: t("common.unable-to-open-print-window"),
        type: "error",
      });
    }
  };

  const handleSubmitReceiptUpload = (payload: {
    receiptNumber: string;
    receiptDate: string;
    receiptAmount: string;
    receiptFile: File;
  }) => {
    const uploadedReceiptUrl = receiptAttachmentUrlRef.current ?? receiptAttachmentUrl;

    if (!uploadedReceiptUrl) {
      notify({
        title: t("licensing.notifications.receipt-upload-error-title"),
        description: t("common.something-went-wrong"),
        type: "error",
      });
      return;
    }

    receiptSubmitMutation.mutate({
      receiptNumber: payload.receiptNumber,
      receiptDate: payload.receiptDate,
      receiptAmount: payload.receiptAmount,
      receiptDocumentUrl: uploadedReceiptUrl,
    });
  };

  const details = licenseResponse ? buildVendorLicenseDetails(licenseResponse) : null;
  const refOrderNo = details?.refOrderNo;

  const effectiveStatus = fakeStatus ?? details?.status;
  const shouldShowPortOperatorAccessPeriod =
    effectiveStatus === LICENSE_STATUSES.AWAITING_MANAGING_DIRECTOR_APPROVAL;
  const { badgeType, label: status } = getLicenseStatusFormat(effectiveStatus, t);
  const effectiveTaskDecision = fakeTaskDecision ?? licenseResponse?.taskDecision ?? null;
  const effectiveCompanyId = licenseResponse?.companyId ?? effectiveTaskDecision?.companyId ?? null;
  const isSameCompanyReceiptUploader = Boolean(
    user?.companyId && effectiveCompanyId && user.companyId === effectiveCompanyId,
  );
  const shouldShowReceiptRibbon =
    (isVendorMaster || isSameCompanyReceiptUploader) &&
    shouldUsePortOperatorEndpoint &&
    canUserUploadReceipt(effectiveStatus);

  const hasViewDetails = hasStatusDetails(effectiveTaskDecision);

  const companyItems = getCompanyDetailsItems({
    details,
    TEST_ID_BASE,
    transStyleClass,
    wrapperStyleClass,
    wrapperFirstStyleClass,
    t,
  });

  const applicantItems = getApplicantDetailsItems({
    details,
    TEST_ID_BASE,
    transStyleClass,
    wrapperStyleClass,
    wrapperFirstStyleClass,
    t,
  });

  const tradeItems = getTradeLicenseItems({
    details,
    TEST_ID_BASE,
    transStyleClass,
    wrapperStyleClass,
    wrapperFirstStyleClass,
    t,
  });

  const additionalInformation = getAdditionalInformationItems({
    details,
    TEST_ID_BASE,
    transStyleClass,
    wrapperStyleClass,
    wrapperFirstStyleClass,
    t,
  });

  const documentCards = sortLicenseDocumentsForDisplay(details?.documents ?? [])
    .filter((document) => Boolean(document?.documentUrl))
    .map((document, index) => {
      const documentType = document.documentType || `document ${index + 1}`;
      const extension = getFileExtensionFromUrl(document.documentUrl);
      const fallbackFileNameBase = documentType || `document-${index + 1}`;
      const fallbackFileName = extension
        ? `${fallbackFileNameBase}.${extension}`
        : fallbackFileNameBase;
      const fileName = document.originalFileName || fallbackFileName;

      return {
        testId: `${TEST_ID_BASE}-document-${index}`,
        title: getDocumentTitle(documentType, t),
        fileName,
        fileUrl: document.documentUrl,
      };
    });

  const receiptDocumentUrl = details?.receipt?.receiptDocumentUrl;
  const receiptAlreadyIncluded = documentCards.some(
    (document) => document.fileUrl === receiptDocumentUrl,
  );

  if (receiptDocumentUrl && !receiptAlreadyIncluded) {
    const receiptExtension = getFileExtensionFromUrl(receiptDocumentUrl);
    const receiptFileName = receiptExtension ? `receipt.${receiptExtension}` : "receipt";

    documentCards.push({
      testId: `${TEST_ID_BASE}-document-receipt`,
      title: t("licensing.upload-receipt"),
      fileName: receiptFileName,
      fileUrl: receiptDocumentUrl,
    });
  }

  const licenseFlowData = licenseResponse
    ? {
        id: id!,
        companyId: licenseResponse.companyId ?? null,
        status: fakeStatus ?? licenseResponse.status,
        task: licenseResponse.task || null,
        taskDecision: effectiveTaskDecision,
      }
    : undefined;
  const breadcrumbLabel = isPortOperatorContext
    ? t("licensing.port-operator-licenses-title")
    : isServiceDesk
      ? t("licensing.service-desk-license-title")
      : t("licensing.title");

  if (isLicenseError && isForbidden(licenseError)) {
    return <UnauthorizedRedirection />;
  }

  if (isLicenseError) {
    return <ErrorHandler error={licenseError} testId={`${TEST_ID_BASE}-error-handler`} />;
  }

  return (
    <PageLayout dir={dir}>
      <Subheader
        testId={`${TEST_ID_BASE}-subheader`}
        subtitle={
          <div
            className="flex items-center space-x-2 text-sm text-gray-700"
            data-testid={`${TEST_ID_BASE}-breadcrumb`}
          >
            <Link
              to={licensesBasePath}
              className="text-blue-600 hover:underline"
              data-testid={`${TEST_ID_BASE}-breadcrumb-link`}
            >
              {breadcrumbLabel}
            </Link>
            <span className="text-gray-400">/</span>
            <span data-testid={`${TEST_ID_BASE}-breadcrumb-current`}>{refOrderNo}</span>
          </div>
        }
        title={
          <div className="flex items-end justify-between w-full">
            <div className="w-full">
              <div data-testid={`${TEST_ID_BASE}-title`}>
                {shouldUsePortOperatorEndpoint
                  ? t("licensing.port-operator-license-title")
                  : t("licensing.vendor-license-title")}
              </div>
              <div className="mt-2 flex items-center min-h-10 text-sm font-normal">
                <div className="min-w-40 flex items-center">
                  <Badge type={badgeType} label={status} />

                  {hasViewDetails && (
                    <>
                      <span className="mx-1 text-gray-500 font-normal">-</span>
                      <Link
                        to="#"
                        onClick={(e) => {
                          e.preventDefault();
                          openDrawer("detailsDrawer");
                        }}
                        className="font-medium text-blue-800 hover:underline cursor-pointer"
                        data-testid={`${TEST_ID_BASE}-view-details-link`}
                      >
                        {t("common.view-details")}
                      </Link>
                    </>
                  )}
                </div>
              </div>
            </div>

            <LicenseFlowButtons
              refetchLicense={refetchLicense}
              licenseFlowData={licenseFlowData}
              isPortOperatorContext={isPortOperatorContext}
              isPortOperatorLicense={shouldUsePortOperatorEndpoint}
              isVendorMasterContext={isVendorMasterContext}
              openDrawer={openDrawer}
              onPrint={handlePrint}
            />
          </div>
        }
      />
      <div data-testid={TEST_ID_BASE} className="flex flex-col w-full h-full p-8 px-24">
        {shouldShowReceiptRibbon && (
          <div className="mb-4">
            <ActionRibbon
              testId={`${TEST_ID_BASE}-receipt-ribbon`}
              title={t("licensing.receipt-ribbon-title")}
              description={t("licensing.receipt-ribbon-description")}
              action={
                <AddReceiptButton
                  langBase="licensing"
                  isActionAvailable={true}
                  onAddReceipt={() => openDrawer("receiptUploadDrawer")}
                />
              }
            />
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
          <div className="grid gap-4">
            <Paper variant="outlined">
              <div data-testid={`${TEST_ID_BASE}-section-company-details`} className="p-4">
                <p className="font-bold">{t("licensing.companyDetails")}</p>
                {companyItems.map((item) => (
                  <PaperItem
                    key={item.testId}
                    testId={item.testId}
                    translate={item.translate}
                    textStyles={item.textStyles}
                    tranStyle={item.tranStyle}
                    wrapperStyle={item.wrapperStyle}
                    text={item.text}
                  />
                ))}
              </div>
            </Paper>

            {!!additionalInformation.length && (
              <div>
                <p className="mb-2">{t("licensing.additional-information")}</p>

                <Paper variant="outlined">
                  <div data-testid={`${TEST_ID_BASE}-section-receipt-information`} className="p-4">
                    <p className="font-bold">{t("licensing.receipt-information")}</p>
                    {additionalInformation.map((item) => (
                      <PaperItem
                        key={item.testId}
                        testId={item.testId}
                        translate={item.translate}
                        textStyles={item.textStyles}
                        tranStyle={item.tranStyle}
                        wrapperStyle={item.wrapperStyle}
                        text={item.text}
                      />
                    ))}
                  </div>
                </Paper>
              </div>
            )}
          </div>

          <div className="grid gap-4">
            <Paper variant="outlined">
              <div data-testid={`${TEST_ID_BASE}-section-applicant-details`} className="p-4">
                <p className="font-bold">{t("licensing.applicantDetails")}</p>
                {applicantItems.map((item) => (
                  <PaperItem
                    key={item.testId}
                    testId={item.testId}
                    translate={item.translate}
                    textStyles={item.textStyles}
                    tranStyle={item.tranStyle}
                    wrapperStyle={item.wrapperStyle}
                    text={item.text}
                  />
                ))}
              </div>
            </Paper>

            <Paper variant="outlined">
              <div data-testid={`${TEST_ID_BASE}-section-trade-license`} className="p-4">
                <p className="font-bold">{t("licensing.tradeLicense")}</p>
                {tradeItems.map((item) => (
                  <PaperItem
                    key={item.testId}
                    testId={item.testId}
                    translate={item.translate}
                    textStyles={item.textStyles}
                    tranStyle={item.tranStyle}
                    wrapperStyle={item.wrapperStyle}
                    text={item.text}
                  />
                ))}
              </div>
            </Paper>
          </div>
        </div>

        <div className="mt-2">
          <p className="mb-2">{t("licensing.documents")}</p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {documentCards.map((document) => (
              <LicenseCard
                key={document.testId}
                testId={document.testId}
                title={document.title}
                fileName={document.fileName}
                fileUrl={document.fileUrl}
                onPreview={() => handlePreview("bff-licensing", document.fileUrl)}
                onDownload={() =>
                  handleDownload("bff-licensing", document.fileUrl, document.fileName)
                }
              />
            ))}
          </div>
        </div>
      </div>

      <RejectLicenseDrawer
        isOpen={openDrawers.rejectDrawer}
        onClose={(drawerName) => {
          if (drawerName === "rejectDrawer") {
            clearRejectionAttachmentUrl();
          }
          closeDrawer(drawerName);
        }}
        refOrderNo={refOrderNo!}
        isPortOperatorContext={isPortOperatorContext}
        onSubmit={handleRejectLicense}
        onDocumentUpload={handleRejectionDocumentUpload}
        isUploading={rejectionUploadMutation.isPending}
        isSubmitting={isCompletingLicense}
      />

      {licenseResponse &&
        (isPortOperatorContext ? (
          <ApprovePortOperatorLicenseDrawer
            isOpen={openDrawers.approveDrawer}
            onClose={(drawerName) => {
              if (drawerName === "approveDrawer") {
                clearApprovalAttachmentUrl();
              }
              closeDrawer(drawerName);
            }}
            refOrderNo={refOrderNo!}
            tradingLicenseValidityDate={licenseResponse.tradingLicenseValidityDate}
            vendorLicenseExpiryDate={
              licenseResponse.vendorLicenseExpiryDate ?? licenseResponse.tradingLicenseValidityDate
            }
            licenseStatus={licenseResponse.status}
            showAccessPeriodFields={shouldShowPortOperatorAccessPeriod}
            onSubmit={handleApprovePortOperatorLicense}
            onDocumentUpload={handleApprovalDocumentUpload}
            isUploading={approvalUploadMutation.isPending}
            isSubmitting={isCompletingLicense}
          />
        ) : (
          <ApproveLicenseDrawer
            isOpen={openDrawers.approveDrawer}
            onClose={(drawerName) => {
              if (drawerName === "approveDrawer") {
                clearApprovalAttachmentUrl();
              }
              closeDrawer(drawerName);
            }}
            refOrderNo={refOrderNo!}
            tradingLicenseValidityDate={licenseResponse.tradingLicenseValidityDate}
            vendorLicenseExpiryDate={
              licenseResponse.vendorLicenseExpiryDate ?? licenseResponse.tradingLicenseValidityDate
            }
            onSubmit={handleApproveLicense}
            onDocumentUpload={handleApprovalDocumentUpload}
            isUploading={approvalUploadMutation.isPending}
            isSubmitting={isCompletingLicense}
            prefilledCompanyId={
              isServiceDeskContext && licenseResponse.companyId
                ? licenseResponse.companyId
                : undefined
            }
          />
        ))}

      <ReceiptUploadDrawer
        isOpen={openDrawers.receiptUploadDrawer}
        onClose={(drawerName) => {
          if (drawerName === "receiptUploadDrawer") {
            clearReceiptAttachmentUrl();
          }
          closeDrawer(drawerName);
        }}
        onSubmit={handleSubmitReceiptUpload}
        onDocumentUpload={handleReceiptDocumentUpload}
        isUploading={receiptUploadMutation.isPending}
        isSubmitting={receiptSubmitMutation.isPending}
      />

      <StatusDetailsDrawer
        isOpen={openDrawers.detailsDrawer}
        onClose={closeDrawer}
        taskDecision={effectiveTaskDecision}
      />
    </PageLayout>
  );
};

export default LicenseDetails;
